// client/src/dnd/views/Interactive_parts/ViewerPathingLoader.js
import React, { useEffect, useRef } from "react";
import { useMap } from "react-leaflet";
import L from "leaflet";

const apiBase = "/api/polygons";

function ViewerPathingLoader({ mapId, onPathingEntriesChange }) {
  const map = useMap();
  const fgRef = useRef(null);
  const layersByIdRef = useRef({});

  useEffect(() => {
    if (!map || !mapId) return;
    let cancelled = false;

    async function loadShapes() {
      try {
        const res = await fetch(`${apiBase}?mapId=${mapId}`);
        const shapes = await res.json();
        if (cancelled) return;

        if (fgRef.current) {
          fgRef.current.clearLayers();
        } else {
          fgRef.current = L.featureGroup().addTo(map);
        }
        layersByIdRef.current = {};

        const entries = [];

        shapes.forEach(shapeRaw => {
          const rawBehavior =
            shapeRaw.behaviorType ??
            shapeRaw.behavior_type ??
            null;
          if (rawBehavior !== "pathing") {
            return;
          }

          let geometry;
          try {
            geometry =
              typeof shapeRaw.geometry === "string"
                ? JSON.parse(shapeRaw.geometry)
                : shapeRaw.geometry;
          } catch {
            return;
          }

          let layer;
          if (
            geometry &&
            geometry.type === "Point" &&
            typeof shapeRaw.radius === "number" &&
            geometry.coordinates
          ) {
            const coords = geometry.coordinates;
            layer = L.circle([coords[1], coords[0]], {
              radius: shapeRaw.radius,
              color: shapeRaw.strokeColor || shapeRaw.stroke_color || "#3388ff",
              fillColor: shapeRaw.fillColor || shapeRaw.fill_color || "#3388ff",
              opacity: 0,
              fillOpacity: 0,
              weight: 0,
              interactive: false,
            });
          } else {
            const geojsonFeature = {
              type: "Feature",
              geometry,
              properties: {
                behaviorType: "pathing",
                behavior_type: "pathing",
              },
            };
            const layers = L.geoJSON(geojsonFeature, {
              pointToLayer: (_feature, latlng) =>
                L.circleMarker(latlng, {
                  radius: 10,
                  fillColor: shapeRaw.fillColor || shapeRaw.fill_color || "#3388ff",
                  color: shapeRaw.strokeColor || shapeRaw.stroke_color || "#3388ff",
                  opacity: 0,
                  fillOpacity: 0,
                  weight: 0,
                  interactive: false,
                }),
              style: {
                color: shapeRaw.strokeColor || shapeRaw.stroke_color || "#3388ff",
                fillColor: shapeRaw.fillColor || shapeRaw.fill_color || "#3388ff",
                opacity: 0,
                fillOpacity: 0,
                weight: 0,
                interactive: false,
              },
            }).getLayers();
            layer = layers[0];
          }

          if (!layer) return;

          const meta = {
            ...shapeRaw,
            behaviorType: "pathing",
            behavior_type: "pathing",
          };

          layer._dbId = shapeRaw.id;
          layer._shapeMeta = meta;
          layer.options.behaviorType = "pathing";
          layer.options.behavior_type = "pathing";
          layer.options.opacity = 0;
          layer.options.fillOpacity = 0;
          layer.options.weight = 0;
          layer.options.interactive = false;

          if (typeof layer.unbindPopup === "function") {
            layer.unbindPopup();
          }

          fgRef.current.addLayer(layer);
          layersByIdRef.current[shapeRaw.id] = layer;

          entries.push({
            id: Number(shapeRaw.id),
            shape: meta,
            layer,
          });
        });

        if (typeof onPathingEntriesChange === "function") {
          onPathingEntriesChange(entries);
        }
      } catch {
        if (typeof onPathingEntriesChange === "function") {
          onPathingEntriesChange([]);
        }
      }
    }

    loadShapes();

    return () => {
      cancelled = true;
      if (fgRef.current && map) {
        map.removeLayer(fgRef.current);
        fgRef.current = null;
      }
      layersByIdRef.current = {};
      if (typeof onPathingEntriesChange === "function") {
        onPathingEntriesChange([]);
      }
    };
  }, [map, mapId, onPathingEntriesChange]);

  return null;
}

export default ViewerPathingLoader;
